import type { FC } from 'react';
import React, { useContext, useCallback } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
import type { ApolloError } from 'apollo-client';

import { token } from '@atlaskit/tokens';
import { N200 } from '@atlaskit/theme/colors';
import LikeIcon from '@atlaskit/icon/glyph/like';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import Button from '@atlaskit/button/standard-button';

import { ErrorDisplay } from '@confluence/error-boundary';
import { DelayedReactions, ReactionLocation, ReactionContainerType } from '@confluence/reactions';
import { ReactionsContext } from '@confluence/comment-context';

import { useIsReactionsInFeedEnabled } from '../feedReactionsFeatureFlags';

import { FeedCardContext } from './FeedCardContext';
import type { PopularFeedTimeGranularityValue } from '../../Feed';

const i18n = defineMessages({
	like: {
		id: 'feed.card.like',
		defaultMessage: 'Like',
		description: 'Text to be displayed on the like button in the card',
	},
	unlike: {
		id: 'feed.card.unlike',
		defaultMessage: 'Unlike',
		description: 'Text to be displayed on the unlike button in the card',
	},
	likes: {
		id: 'feed.card.likes.label',
		defaultMessage: 'Likes',
		description: 'label for thumbs up icon that represents likes on the page',
	},
	comments: {
		id: 'feed.card.comments.label',
		defaultMessage: 'Comments',
		description: 'label for speech bubble icon that represents comments on the page',
	},
	likeMutationErrorTitle: {
		id: 'feed.card.like.mutation.error.title',
		defaultMessage: 'We’re having trouble liking that page',
		description:
			'Title of error flag shown when a user attempts to like the feed card but the action fails.',
	},
	unlikeMutationErrorTitle: {
		id: 'feed.card.unlike.mutation.error.title',
		defaultMessage: 'We’re having trouble unliking that page',
		description:
			'Title of error flag shown when a user attempts to unlike the feed card but the action fails.',
	},
	likeMutationErrorDescription: {
		id: 'feed.card.like.mutation.error.description',
		defaultMessage: 'Try it again.',
		description:
			'Body of error flag shown when a user attempts to like/unlike the feed card but the action fails. Shown below the error title',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled(FlexWrapper)({
	color: token('color.text.subtle', N200),
	fontWeight: 500,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReactionsWrapper = styled(FlexWrapper)<{
	isGlobalReactionsAndLikesEnabled?: boolean;
}>({
	color: token('color.text.subtle', N200),
	fontWeight: 500,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: (props) =>
		props.isGlobalReactionsAndLikesEnabled ? 'space-between' : 'flex-end',
	alignItems: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LikesCountWrapper = styled.div({
	display: 'flex',
	marginRight: token('space.075', '6px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentsWrapper = styled.div({
	display: 'flex',
	paddingLeft: token('space.025', '2px'),
	paddingBottom: token('space.050', '4px'),
	justifyContent: 'right',
	minWidth: '115px',
	fontWeight: 400,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span[role="img"]': {
		paddingRight: token('space.050', '4px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HorizontalSpacer = styled.div({
	margin: `0 ${token('space.075', '6px')}`,
});

const HorizontalSeparator = () => <HorizontalSpacer>•</HorizontalSpacer>;

export enum ShowCommentState {
	Number,
	NoShow,
	ShowComment,
	HideComment,
}

type Props = {
	userLiked: boolean;
	handleLikeClick: () => void;
	numLikes: number;
	numComments: number;
	contentId: string;
	contentType: string;
	spaceId?: string;
	likeMutationError: ApolloError | undefined;
	showCommentState: ShowCommentState;
	onCommentToggleClick: () => void;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
};

const FeedCardActionCountsComponent: FC<Props & WrappedComponentProps> = ({
	userLiked,
	handleLikeClick,
	numLikes,
	numComments,
	contentId,
	contentType,
	spaceId,
	intl,
	likeMutationError,
	showCommentState,
	onCommentToggleClick,
	popularFeedTimeGranularity,
}) => {
	const { fireAnalyticsEvent } = useContext(FeedCardContext);
	const { isReactionsEnabled: isGlobalReactionsAndLikesEnabled } = useContext(ReactionsContext);
	const isReactionsInFeedEnabled = useIsReactionsInFeedEnabled();

	const onLikeClick = useCallback(() => {
		handleLikeClick();
		fireAnalyticsEvent({
			type: 'sendTrackEvent',
			action: userLiked ? 'deleted' : 'created',
			actionSubject: 'like',
			actionSubjectId: 'content',
			additionalAttributes: {
				popularFeedTimeGranularity,
			},
		});
	}, [handleLikeClick, userLiked, fireAnalyticsEvent, popularFeedTimeGranularity]);

	const onToggleClick = useCallback(() => {
		onCommentToggleClick();
		fireAnalyticsEvent({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId:
				showCommentState === ShowCommentState.ShowComment ? 'show-comment' : 'hide-comment',
		});
	}, [onCommentToggleClick, fireAnalyticsEvent, showCommentState]);

	const likeButtonMessage = userLiked ? i18n.unlike : i18n.like;
	const location =
		contentType === 'page' ? ReactionLocation.FEED_PAGE : ReactionLocation.FEED_BLOGPOST;

	const renderLikesAndComments = (
		<Wrapper data-testid="feed-card-action-counts">
			{isGlobalReactionsAndLikesEnabled && (
				<>
					<Button onClick={onLikeClick} appearance="subtle-link" spacing="none">
						<FormattedMessage {...likeButtonMessage} />
					</Button>

					{(!!numLikes || !!numComments) && <HorizontalSeparator />}

					{!!numLikes && (
						<LikesCountWrapper data-testid="content-likes-count">
							<LikeIcon label={intl.formatMessage(i18n.likes)} size="small" />
							{numLikes}
						</LikesCountWrapper>
					)}
					{likeMutationError && <ErrorDisplay error={likeMutationError} />}
				</>
			)}

			{!!numComments && (
				<FlexWrapper data-testid="content-comments-count">
					<CommentIcon label={intl.formatMessage(i18n.comments)} size="small" />
					{numComments}
				</FlexWrapper>
			)}
		</Wrapper>
	);

	const commentToggle =
		showCommentState === ShowCommentState.ShowComment ? (
			<FormattedMessage
				id="feed.comment.expand"
				defaultMessage="Show comment"
				description="Expand the comment section on feed card"
			/>
		) : (
			<FormattedMessage
				id="feed.comment.collapse"
				defaultMessage="Hide comment"
				description="Collapse the comment section on feed card"
			/>
		);

	const commentCount = !!numComments && (
		<CommentsWrapper>
			<CommentIcon label={intl.formatMessage(i18n.comments)} size="small" />
			<FormattedMessage
				id="feed.comment.count"
				defaultMessage="{numComments, plural, one {1 comment} other {# comments}}"
				description="Feed comment count label"
				values={{ numComments }}
			/>
		</CommentsWrapper>
	);

	const optionalCommentToggle = showCommentState > ShowCommentState.NoShow && (
		<Button
			onClick={onToggleClick}
			appearance="subtle-link"
			spacing="none"
			iconBefore={<CommentIcon label={intl.formatMessage(i18n.comments)} size="small" />}
		>
			{commentToggle}
		</Button>
	);

	const renderComments =
		showCommentState == ShowCommentState.Number ? commentCount : optionalCommentToggle;

	const renderReactionsAndComments = contentId && contentType && spaceId && (
		<ReactionsWrapper
			isGlobalReactionsAndLikesEnabled={isGlobalReactionsAndLikesEnabled}
			data-testid="feed-content-reactions"
		>
			{isGlobalReactionsAndLikesEnabled && (
				<DelayedReactions
					contentId={contentId}
					location={location}
					containerId={spaceId}
					containerType={ReactionContainerType.SPACE}
				/>
			)}
			{renderComments}
		</ReactionsWrapper>
	);

	return (
		<small>{isReactionsInFeedEnabled ? renderReactionsAndComments : renderLikesAndComments}</small>
	);
};

export const FeedCardActionCounts = injectIntl(FeedCardActionCountsComponent);
